import * as Tone from 'tone';
import { getSetting, setSetting, subscribeUser } from '../data/userStore';

export const MIX_CHANNELS = [
  { id: 'chords', label: 'Chords' },
  { id: 'piano', label: 'Fretboard piano' },
  { id: 'backing', label: 'Backing' },
  { id: 'metronome', label: 'Metronome' },
  { id: 'master', label: 'Master volume' },
];
const key = 'upScale.playbackMix';
const live = {};
const listeners = new Set();
let version = 0;
export const MIX_MIN_DB = -40;
export const MIX_MAX_DB = 6;
export const MIX_SILENCE_DB = -41;
const channelId = (id) => id === 'drone' ? 'chords' : id;
const normalize = (value) => ({
  db: Number.isFinite(value?.db) ? Math.max(MIX_SILENCE_DB, Math.min(MIX_MAX_DB, value.db)) : 0,
  muted: value?.muted === true,
});
export const formatMixDb = (db) => db <= MIX_SILENCE_DB ? 'Muted' : `${db > 0 ? '+' : ''}${Number(db.toFixed(1))} dB`;
const readChannel = (id) => {
  const saved = getSetting(key, {})?.[id];
  if (Number.isFinite(saved?.db)) return normalize(saved);
  if (id === 'master') return normalize();
  const level = Number.isFinite(saved?.level) ? Math.max(0, Math.min(100, saved.level)) : 100;
  let db = level > 0 ? 40 * Math.log10(level / 100) : MIX_SILENCE_DB;
  const master = getSetting('masterVolume', 0);
  if (id !== 'backing' && Number.isFinite(master)) db += master;
  if (id === 'piano') {
    const piano = getSetting('pianoVolumeDb', 6);
    if (Number.isFinite(piano)) db = piano <= -40 ? MIX_SILENCE_DB : db + piano - 6;
  }
  return normalize({ db: db < MIX_MIN_DB ? MIX_SILENCE_DB : Math.round(db * 10) / 10, muted: saved?.muted });
};
export const getMixChannel = (id) => normalize(live[channelId(id)] ?? readChannel(channelId(id)));
const channelGain = (id) => {
  const { db, muted } = getMixChannel(id);
  return muted || db <= MIX_SILENCE_DB ? 0 : 10 ** (db / 20);
};
export const getMixGain = (id) => channelGain(id) * (id === 'master' ? 1 : channelGain('master'));
export const getMixVersion = () => version;
export const subscribeMix = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const emit = () => {
  version += 1;
  listeners.forEach(listener => listener());
};
subscribeUser(emit);
export const previewMixChannel = (id, changes) => {
  live[id] = normalize({ ...getMixChannel(id), ...changes });
  emit();
};
export const commitMixChannel = (id, changes = {}) => {
  const channel = normalize({ ...getMixChannel(id), ...changes });
  delete live[id];
  setSetting(key, { ...getSetting(key, {}), [id]: channel });
};
export const resetMix = () => {
  MIX_CHANNELS.forEach(({ id }) => delete live[id]);
  setSetting(key, Object.fromEntries(MIX_CHANNELS.map(({ id }) => [id, { db: 0, muted: false }])));
};
export const createMixBus = (id) => {
  const bus = new Tone.Gain(getMixGain(id));
  const unsubscribe = subscribeMix(() => bus.gain.rampTo(getMixGain(id), 0.03));
  const dispose = bus.dispose.bind(bus);
  bus.dispose = () => { unsubscribe(); return dispose(); };
  return bus;
};
export const createNativeMixBus = (id, context) => {
  const bus = context.createGain();
  bus.gain.value = getMixGain(id);
  const unsubscribe = subscribeMix(() => {
    const now = context.currentTime;
    bus.gain.cancelScheduledValues(now);
    bus.gain.setTargetAtTime(getMixGain(id), now, 0.01);
  });
  bus.dispose = () => { unsubscribe(); bus.disconnect(); };
  return bus;
};
const mediaNodes = new WeakMap();
export const bindBackingMedia = (element) => {
  if (!element) return undefined;
  let unsubscribe;
  const prepare = () => {
    let entry = mediaNodes.get(element);
    if (!entry) {
      const context = Tone.getContext().rawContext;
      const source = context.createMediaElementSource(element);
      const gain = context.createGain();
      gain.gain.value = getMixGain('backing');
      source.connect(gain);
      gain.connect(context.destination);
      entry = { context, gain };
      mediaNodes.set(element, entry);
    }
    const apply = () => {
      entry.gain.gain.setTargetAtTime(getMixGain('backing'), entry.context.currentTime, 0.01);
    };
    apply();
    if (!unsubscribe) unsubscribe = subscribeMix(apply);
    Tone.start().catch(() => {});
  };
  element.addEventListener('play', prepare);
  if (mediaNodes.has(element) || !element.paused) prepare();
  return () => {
    element.removeEventListener('play', prepare);
    if (unsubscribe) unsubscribe();
  };
};
